"use client";

import * as React from "react";
import { Percent } from "lucide-react";
import { cn } from "@/lib/utils";
import Section from "./Section";
import {
  averagePracticalPassRate,
  type RoundDifficulty,
  type RoundStats,
} from "@/lib/pass-rate-data";

/**
 * 회차 풀이 페이지 우측 사이드바 하단 회차 통계 카드 (JCG-2167).
 *
 * `/past-exam/<YYYY>/<slug>` 에서 그 회차의 실기 합격률·체감 난이도·한 줄 코멘트를 컴팩트하게
 * 보여준다. 좁은 우측 사이드바(약 320~372px)에 맞춰 큰 합격률 숫자 + 칩/배지로 요약하며,
 * 칩 색상은 PassRateMiniChart 와 동일한 디자인 토큰 페어로 다크모드 자동 대응.
 *
 * 합격률 미발표 최신 회차(passRate=null)는 "집계 전"으로 표기한다.
 */

// 합격률 칩/배지 색 페어 — 높은 합격률(쉬움)=emerald, 낮은 합격률(어려움)=red.
// PassRateMiniChart 칩과 동일한 디자인 토큰 페어로 다크모드 자동 대응.
const CLS_EASY =
  "bg-emerald-50 text-emerald-600 dark:bg-emerald-950/40 dark:text-emerald-300";
const CLS_HARD =
  "bg-red-50 text-red-600 dark:bg-red-950/40 dark:text-red-300";

/** 난이도 배지 — 쉬움=emerald, 보통=amber, 어려움=red (낮은 합격률=빨강). */
const DIFFICULTY_BADGE: Record<RoundDifficulty, string> = {
  쉬움: CLS_EASY,
  보통: "bg-amber-50 text-amber-600 dark:bg-amber-950/40 dark:text-amber-300",
  어려움: CLS_HARD,
};

/** 전체 평균 대비 칩. 0.5%p 이내 차이는 "평균 수준". null(집계 전)이면 칩 없음. */
function getCompareChip(diff: number | null): { text: string; cls: string } | null {
  if (diff === null) return null;
  if (Math.abs(diff) < 0.5)
    return { text: "평균 수준", cls: "bg-muted text-muted-foreground" };
  return diff > 0
    ? { text: "평균보다 높아요", cls: CLS_EASY }
    : { text: "평균보다 낮아요", cls: CLS_HARD };
}

export default function RoundStatsCard({ stats }: { stats: RoundStats }) {
  const [open, setOpen] = React.useState(true);
  const { name, passRate, difficulty, comment } = stats;

  // 전체 평균 대비(발표 회차만). 0.5%p 이내 차이는 "평균 수준"으로 본다.
  const diff = passRate === null ? null : passRate - averagePracticalPassRate;
  const compareChip = getCompareChip(diff);

  return (
    <Section
      icon={Percent}
      label="이 회차 합격률"
      amber
      open={open}
      onToggle={() => setOpen((v) => !v)}
      right={
        difficulty ? (
          <span
            className={cn(
              "rounded-md px-1.5 py-0.5 text-[11px] font-bold",
              DIFFICULTY_BADGE[difficulty],
            )}
          >
            {difficulty}
          </span>
        ) : undefined
      }
    >
      <div className="flex flex-col gap-2 pt-1">
        {/* 합격률 큰 숫자 */}
        <div className="flex flex-wrap items-baseline gap-x-1.5 gap-y-0.5">
          {passRate === null ? (
            <span className="text-base font-bold text-muted-foreground">
              집계 전
            </span>
          ) : (
            <span className="text-2xl font-black tabular-nums text-foreground">
              {passRate}%
            </span>
          )}
          <span className="text-[11px] font-medium text-muted-foreground">
            {name} 실기 합격률
          </span>
        </div>

        {/* 평균 대비 칩 */}
        {compareChip && (
          <div className="flex flex-wrap items-center gap-1 text-[11px] font-semibold">
            <span className={cn("rounded-md px-1.5 py-0.5", compareChip.cls)}>
              {compareChip.text}
            </span>
            <span className="rounded-md bg-muted px-1.5 py-0.5 text-muted-foreground">
              전체 평균 {averagePracticalPassRate}%
            </span>
          </div>
        )}

        {/* 한 줄 코멘트 */}
        {comment && (
          <p className="text-[11px] leading-snug text-muted-foreground">
            {comment}
          </p>
        )}
      </div>
    </Section>
  );
}
